import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { Store } from '@ngrx/store';
import { filter, take } from 'rxjs/operators';

import { initializeCreateUserState } from 'src/app/actions/user.actions';
import { dispatchInfoMessage } from 'src/app/actions/core.actions';
import { createDaigaku, initializeCreateDaigakuState } from 'src/app/actions/daigaku.actions';
import { RootState } from 'src/app/reducers';
import { getCreateDaigakuResult } from 'src/app/selectors/daigaku.selectors';

import { Authority, HighSchool, Prefecture } from 'src/app/models/common-data';
import { CreateDaigakuData } from '../../../models/daigaku';
import { Log } from 'src/app/utils/log';
import { enter } from 'src/app/resources/animations';
import { PREFECTURES } from '../../../resources/config';

@Component({
  selector: 'app-add-daigaku-dialog',
  templateUrl: './add-daigaku-dialog.component.html',
  styleUrls: ['./add-daigaku-dialog.component.scss'],
  animations: [enter]
})
export class AddDaigakuDialogComponent implements OnInit, OnDestroy {
  userAuthorityObunshaId: number;
  daigakuForm: UntypedFormGroup;
  highSchools: HighSchool[];
  prefectures: Prefecture[];
  authorities: Authority[];
  defaultOrganization: string;
  defaultSchool: string;
  defaultSchoolId: string;
  schoolVisibleFlag: boolean;
  dialogTypeAccounts: string;
  dialogType: string;
  dialogTitle: string;
  daigakuCreating = false;
  errorMessage = '';
  private LOG_SOURCE = this.constructor.name;

  /** two-way binding */
  adminDisabled = true;

  constructor(private store: Store<RootState>, private dialogRef: MatDialogRef<AddDaigakuDialogComponent>) {}

  ngOnInit() {
    this.prefectures = PREFECTURES;
    this.dialogTitle = '大学を登録';

    this.setUpForms();
  }

  ngOnDestroy() {
    this.store.dispatch(initializeCreateUserState());
  }

  createDaigaku() {
    if (this.daigakuForm.invalid) {
      this.daigakuForm.markAllAsTouched();
      return;
    }
    Log.debug(this.LOG_SOURCE, '大学を作成します');
    this.disableForms();
    this.errorMessage = '';

    const daigakuName: string = this.daigakuForm.get('daigakuName').value;
    const daigakuSubName: string = this.daigakuForm.get('daigakuSubName').value;
    const daigakuCode: string = this.daigakuForm.get('daigakuCode').value;
    const prefectureId: string = this.daigakuForm.get('prefectureId').value;

    const daigaku: CreateDaigakuData = {
      daigakuName,
      daigakuSubName,
      daigakuCode,
      prefectureId
    };

    this.store.dispatch(createDaigaku({ daigaku }));

    this.store
      .select(getCreateDaigakuResult)
      .pipe(
        filter(it => it != null),
        take(1)
      )
      .subscribe(result => {
        this.store.dispatch(initializeCreateDaigakuState());
        Log.debug(this.LOG_SOURCE, `create daigaku result: `, result);
        if (result.success) {
          const message = `${daigakuName} を登録しました`;
          this.dialogRef.close(true);
          this.store.dispatch(
            dispatchInfoMessage({
              message
            })
          );
          return;
        }

        Log.warn(this.LOG_SOURCE, `create daigaku error: err.code: ${result.error ? result.error.code : 'none'}`, result.error);
        this.enableForms();
        this.errorMessage = result.error ? `[${result.error.code}] ${result.error.message}` : 'エラーが発生しました';
      });
  }

  cancel() {
    this.dialogRef.close(false);
  }

  // set ups or private method ---------------------------------------------------------------
  private setUpForms() {
    this.daigakuForm = new UntypedFormGroup({
      daigakuName: new UntypedFormControl('', [Validators.required]),
      daigakuSubName: new UntypedFormControl('', []),
      daigakuCode: new UntypedFormControl('', [Validators.required, Validators.pattern(/^[0-9]{6}$/)]),
      prefectureId: new UntypedFormControl('', [Validators.required])
    });
  }

  private disableForms() {
    Object.keys(this.daigakuForm.controls).forEach(ctrlName => {
      this.daigakuForm.get(ctrlName).disable();
    });
    this.daigakuCreating = true;
  }

  private enableForms() {
    Object.keys(this.daigakuForm.controls).forEach(ctrlName => {
      this.daigakuForm.get(ctrlName).enable();
    });
    this.daigakuCreating = false;
  }
}
